import {closeDialog, openDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isArray, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  useLazyGetTireSetQuery,
  usePostTiresArchiveMutation,
  usePostTireSetDeliveryNoteMutation,
  usePostTireSetLabelMutation,
  usePostTireSetsSetCancelTransitMutation,
  usePostTireSetsSetReadyMutation,
  usePostTireSetsSetReplacementMutation,
  usePostTireSetsSetRequestTransitMutation,
  usePostTireSetsSetTransitAcceptanceMutation,
  usePostTireSetsSetUnreadyMutation,
  usePostTireSetsUnsetReplacementMutation,
  usePostTiresIssueBulkMutation,
  usePostTiresReceiptMutation,
  usePostTiresTransferToOrderMutation,
} from '@dms/api/core';
import {DOCUMENT_ACTION_COLUMNS} from '@dms/features/document-context';
import i18n from '@dms/i18n';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  handleApiError,
  printPDF,
  refreshDmsDatagrid,
  RequestTireSet,
  TireSetDetail,
} from '@dms/shared';

import type {ActionCallback} from 'features/eag-grid';

import {extractWheelIds} from '../SetsOverview/utils/extractWheelIds';

type TireSetDGValuesProps = {
  tireSeason: {value: string};
  tireOrderId: {value: string};
  tireOrderSetId: {value: string};
  setNumber: {value: string};
  id: string;
};

export function TireSetRequests() {
  const [editingTireSetData, setEditingTireSetData] = useState<{
    tireOrderId: string;
    setId: string;
  } | null>(null);

  const [getTireSetQuery] = useLazyGetTireSetQuery();
  const [postTireSetLabel] = usePostTireSetLabelMutation();
  const [postTireSetDeliveryNote] = usePostTireSetDeliveryNoteMutation();
  const [postTiresArchive] = usePostTiresArchiveMutation();
  const [postTiresTransferToOrder] = usePostTiresTransferToOrderMutation();
  const [postTiresIssue] = usePostTiresIssueBulkMutation();
  const [postTiresReceipt] = usePostTiresReceiptMutation();
  const [postTireSetsSetReady] = usePostTireSetsSetReadyMutation();
  const [postTireSetsSetUnready] = usePostTireSetsSetUnreadyMutation();
  const [postTireSetsSetRequestTransit] = usePostTireSetsSetRequestTransitMutation();
  const [postTireSetsSetTransitAcceptance] = usePostTireSetsSetTransitAcceptanceMutation();
  const [postTireSetsSetCancelTransit] = usePostTireSetsSetCancelTransitMutation();
  const [postTireSetsSetReplacement] = usePostTireSetsSetReplacementMutation();
  const [postTireSetsUnsetReplacement] = usePostTireSetsUnsetReplacementMutation();

  const actionCallback: ActionCallback = ({actionKey, rowData, refreshData}) => {
    const castedRowData = (isArray(rowData) ? rowData[0] : rowData) as TireSetDGValuesProps;
    const castedRowBulkData = (isArray(rowData) ? rowData : [rowData]) as TireSetDGValuesProps[];

    const transferActionCallback: ActionCallback = ({rowId}) => {
      const targetOrderId = Array.isArray(rowId) ? rowId[0] : rowId;

      if (!targetOrderId) {
        return;
      }

      postTiresTransferToOrder({
        targetOrderId,
        body: {
          tireSets: castedRowBulkData.map((row) => ({
            tireOrderId: row.tireOrderId.value,
            tireSetId: row.tireOrderSetId.value,
          })),
        },
      })
        .unwrap()
        .then(() => {
          closeDialog('transferToOrder');
          refreshData();
        })
        .catch(handleApiError);
    };

    match(actionKey)
      .with('redirectDetail', () => {
        setEditingTireSetData({
          tireOrderId: castedRowData.tireOrderId.value,
          setId: castedRowData.tireOrderSetId.value,
        });
      })
      .with('printLabel', () => {
        getTireSetQuery({
          orderId: castedRowData.tireOrderId.value,
          setId: castedRowData.tireOrderSetId.value,
        })
          .unwrap()
          .then((data) => {
            const wheelIds = extractWheelIds(data);

            if (!wheelIds?.length) {
              showNotification.error();
              return;
            }

            postTireSetLabel({
              orderId: castedRowData.tireOrderId.value,
              setId: castedRowData.tireOrderSetId.value,
              body: {
                wheels: wheelIds,
              },
            })
              .unwrap()
              .then((tireLabelData) => {
                const url = tireLabelData?.printout?.pdfUrl;

                if (!url) {
                  showNotification.error();
                  return;
                }

                printPDF(url);
                refreshData();
              })
              .catch(handleApiError);
          })
          .catch(handleApiError);
      })
      .with('printDeliveryNote', () => {
        getTireSetQuery({
          orderId: castedRowData.tireOrderId.value,
          setId: castedRowData.tireOrderSetId.value,
        })
          .unwrap()
          .then((data) => {
            const wheelIds = extractWheelIds(data);

            if (!wheelIds?.length) {
              showNotification.error();
              return;
            }

            postTireSetDeliveryNote({
              orderId: castedRowData.tireOrderId.value,
              setId: castedRowData.tireOrderSetId.value,
              body: {
                wheels: wheelIds,
              },
            })
              .unwrap()
              .then((tireLabelData) => {
                const url = tireLabelData?.printout?.pdfUrl;

                if (!url) {
                  showNotification.error();
                  return;
                }

                printPDF(url);
                refreshData();
              })
              .catch(handleApiError);
          })
          .catch(handleApiError);
      })
      .with('transferToOrder', () => {
        openDialog(
          <DmsDataGrid
            gridCode="tire-set-transfer"
            autoHeight
            actionCallback={transferActionCallback}
          />,
          {
            id: 'transferToOrder',
            title: i18n.t('page.tiresInventory.labels.transferToOrder'),
            size: 'large',
          }
        );
      })
      .with('request', 'editRequest', () => {
        openDialog(
          <RequestTireSet
            tireOrderId={castedRowData.tireOrderId.value}
            tireSetId={castedRowData.tireOrderSetId.value}
            onSave={refreshData}
            onClose={() => closeDialog('requestTireSet')}
          />,
          {
            id: 'requestTireSet',
            title: i18n.t('entity.tireSet.actions.tireSetRequest'),
            size: 'small',
          }
        );
      })
      .with('issue', () => {
        postTiresIssue({
          body: {
            tireSets: castedRowBulkData.map((row) => ({
              orderId: row.tireOrderId.value,
              setId: row.tireOrderSetId.value,
            })),
          },
        })
          .unwrap()
          .then((response) => {
            refreshData();

            if (isNotNilOrEmpty(response?.length === 1 && response[0]?.printout?.pdfUrl)) {
              printPDF(response?.[0]?.printout?.pdfUrl ?? '');
            }
          })
          .catch(handleApiError);
      })
      .with('archive', () => {
        getTireSetQuery({
          orderId: castedRowData.tireOrderId.value,
          setId: castedRowData.tireOrderSetId.value,
        })
          .unwrap()
          .then((data) => {
            const wheelIds = extractWheelIds(data);

            if (!wheelIds?.length) {
              showNotification.error();
              return;
            }

            postTiresArchive({
              orderId: castedRowData.tireOrderId.value,
              setId: castedRowData.tireOrderSetId.value,
              body: {
                wheels: wheelIds,
              },
            })
              .unwrap()
              .then((response) => {
                const url = response?.printout?.pdfUrl;

                if (!url) {
                  showNotification.error();
                  return;
                }

                printPDF(url);
                refreshDmsDatagrid('tire-sets-requests');
              })
              .catch(handleApiError);
          })
          .catch(handleApiError);
      })
      .with('receipt', () => {
        getTireSetQuery({
          orderId: castedRowData.tireOrderId.value,
          setId: castedRowData.tireOrderSetId.value,
        })
          .unwrap()
          .then((data) => {
            const wheelIds = extractWheelIds(data);

            if (!wheelIds?.length) {
              showNotification.error();
              return;
            }

            postTiresReceipt({
              orderId: castedRowData.tireOrderId.value,
              setId: castedRowData.tireOrderSetId.value,
              body: {wheels: wheelIds, withoutPrintout: false},
            })
              .unwrap()
              .then((response) => {
                const url = response?.printout?.pdfUrl;
                refreshDmsDatagrid('tire-sets-requests');

                if (!url) {
                  showNotification.error();
                  return;
                }

                printPDF(url);
              })
              .catch(handleApiError);
          })
          .catch(handleApiError);
      })
      .with('requestReady', () => {
        postTireSetsSetReady({
          body: {
            tireSets: castedRowBulkData.map((row) => ({
              tireOrderId: row.tireOrderId.value,
              tireSetId: row.tireOrderSetId.value,
            })),
          },
        })
          .unwrap()
          .then(() => {
            refreshData();
          })
          .catch(handleApiError);
      })
      .with('requestUnready', () => {
        postTireSetsSetUnready({
          body: {
            tireSets: castedRowBulkData.map((row) => ({
              tireOrderId: row.tireOrderId.value,
              tireSetId: row.tireOrderSetId.value,
            })),
          },
        })
          .unwrap()
          .then(() => {
            refreshData();
          })
          .catch(handleApiError);
      })
      .with('inTransit', () => {
        postTireSetsSetRequestTransit({
          body: {
            tireSets: castedRowBulkData.map((row) => ({
              orderId: row.tireOrderId.value,
              setId: row.tireOrderSetId.value,
            })),
          },
        })
          .unwrap()
          .then((response) => {
            const url = response?.[0]?.printout?.pdfUrl;

            if (!url) {
              showNotification.error();
              return;
            }

            printPDF(url);

            refreshData();
          })
          .catch(handleApiError);
      })
      .with('cancelTransit', () => {
        postTireSetsSetCancelTransit({
          body: {
            tireSets: castedRowBulkData.map((row) => ({
              orderId: row.tireOrderId.value,
              setId: row.tireOrderSetId.value,
            })),
          },
        })
          .unwrap()
          .then(() => {
            refreshData();
          })
          .catch(handleApiError);
      })
      .with('transitAcceptance', () => {
        postTireSetsSetTransitAcceptance({
          body: {
            tireSets: castedRowBulkData.map((row) => ({
              orderId: row.tireOrderId.value,
              setId: row.tireOrderSetId.value,
            })),
          },
        })
          .unwrap()
          .then(() => {
            refreshData();
          })
          .catch(handleApiError);
      })
      .with('setReplacement', () => {
        postTireSetsSetReplacement({
          body: {
            tireSets: castedRowBulkData.map((row) => ({
              orderId: row.tireOrderId.value,
              setId: row.tireOrderSetId.value,
            })),
          },
        })
          .unwrap()
          .then(() => {
            refreshData();
          })
          .catch(handleApiError);
      })
      .with('unsetReplacement', () => {
        postTireSetsUnsetReplacement({
          body: {
            tireSets: castedRowBulkData.map((row) => ({
              orderId: row.tireOrderId.value,
              setId: row.tireOrderSetId.value,
            })),
          },
        })
          .unwrap()
          .then(() => {
            refreshData();
          })
          .catch(handleApiError);
      })
      .with('cancelTransit', () => {})
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const handleCloseEditTireSet = () => {
    setEditingTireSetData(null);
  };

  const handleEditTireSet = () => {
    setEditingTireSetData(null);
    refreshDmsDatagrid('tire-sets-requests');
  };

  return (
    <>
      <DmsDataGrid
        gridCode="tire-set-requests"
        actionCallback={actionCallback}
        predefinedColumns={DOCUMENT_ACTION_COLUMNS}
      />
      {editingTireSetData && (
        <TireSetDetail
          tireOrderId={editingTireSetData.tireOrderId}
          tireSetId={editingTireSetData.setId}
          onClose={handleCloseEditTireSet}
          onEdit={handleEditTireSet}
        />
      )}
    </>
  );
}
